// Sprint 6 — Test Mode Modal: chat embedded per testare il flow draft prima della pubblicazione.
// Riusa l'orchestrator backend via /api/admin/flows/:id/test-session/* (SSE).

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FlowWithRelations } from '@audiq/shared';
import { api } from '../../lib/api';
import { testSessionTurnUrl } from '../../lib/api/flows';
import { ApiClientError } from '../../lib/api/_request';

interface Props {
  flow: FlowWithRelations;
  onClose: () => void;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
}

export function TestModeModal({ flow, onClose }: Props) {
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [sessionEnded, setSessionEnded] = useState<null | string>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const startMut = useMutation({
    mutationFn: () => api.startTestSession(flow.id),
    onSuccess: (resp) => {
      setSessionId(resp.session_id);
      const opening = (resp.flow_snapshot.persona as { opening_message?: string } | undefined)
        ?.opening_message;
      if (opening) {
        setHistory([{ role: 'assistant', content: opening }]);
      }
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => (sessionId ? api.deleteTestSession(flow.id, sessionId) : Promise.resolve()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['test-sessions', flow.id] });
      onClose();
    },
  });

  useEffect(() => {
    // Auto-start on mount
    if (!sessionId && !startMut.isPending && !startMut.isError) {
      startMut.mutate();
    }
  }, [sessionId, startMut]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [history]);

  const sendTurn = async () => {
    if (!sessionId || !input.trim() || streaming || sessionEnded) return;
    const userMsg = input.trim();
    setInput('');
    setHistory((h) => [
      ...h,
      { role: 'user', content: userMsg },
      { role: 'assistant', content: '', streaming: true },
    ]);
    setStreaming(true);
    try {
      const url = testSessionTurnUrl(flow.id, sessionId);
      const resp = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMsg }),
      });
      if (!resp.ok || !resp.body) {
        const errText = await resp.text().catch(() => '');
        throw new Error(`Errore turno: ${resp.status} ${errText}`);
      }
      // Parse SSE manuale
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assembledText = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Eventi SSE separati da \n\n
        const events = buffer.split('\n\n');
        buffer = events.pop() ?? '';
        for (const ev of events) {
          if (!ev.startsWith('data:')) continue;
          const dataStr = ev.slice(5).trim();
          if (!dataStr) continue;
          try {
            const data = JSON.parse(dataStr) as {
              type: string;
              [k: string]: unknown;
            };
            if (data.type === 'text_delta' && typeof data['text'] === 'string') {
              assembledText += data['text'];
              setHistory((h) => {
                const next = [...h];
                const last = next[next.length - 1];
                if (last && last.role === 'assistant') {
                  next[next.length - 1] = { ...last, content: assembledText };
                }
                return next;
              });
            } else if (data.type === 'final' || data.type === 'emergency') {
              if (typeof data['status'] === 'string' && data['status'] !== 'in_progress') {
                setSessionEnded(data['status'] as string);
              }
            }
          } catch {
            // ignora linee malformate
          }
        }
      }
    } catch (err) {
      setHistory((h) => {
        const next = [...h];
        const last = next[next.length - 1];
        if (last && last.role === 'assistant' && last.streaming) {
          next[next.length - 1] = {
            role: 'assistant',
            content: `[errore stream: ${(err as Error).message}]`,
          };
        }
        return next;
      });
    } finally {
      setStreaming(false);
      setHistory((h) => h.map((m) => (m.streaming ? { ...m, streaming: false } : m)));
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(31,36,33,0.55)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          background: 'var(--paper)',
          borderRadius: 14,
          width: 'min(720px, 100%)',
          height: 'min(82vh, 720px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 12px 36px rgba(31,36,33,0.22)',
        }}
      >
        <div
          style={{
            padding: '10px 16px',
            borderBottom: '1px solid var(--line)',
            background: 'color-mix(in oklab, var(--accent) 18%, transparent)',
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <strong className="serif" style={{ color: 'var(--ink)' }}>
                Modalità test
              </strong>
              <span className="mono ms-3" style={{ color: 'var(--mute)', fontSize: 11 }}>
                {flow.name} · sessione isolata, non conservata
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                if (
                  confirm('Terminare il test? La sessione verrà eliminata insieme al transcript.')
                ) {
                  deleteMut.mutate();
                }
              }}
              className="text-sm hover:underline"
              style={{ color: 'var(--danger)' }}
            >
              Termina ed elimina
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            background: 'var(--surface)',
          }}
        >
          {startMut.isPending && (
            <p style={{ color: 'var(--mute)' }} className="mono">
              Avvio sessione…
            </p>
          )}
          {startMut.isError && (
            <p style={{ color: 'var(--danger)' }}>
              {startMut.error instanceof ApiClientError
                ? startMut.error.payload.error
                : 'Errore avvio test session'}
            </p>
          )}
          {history.map((m, i) => (
            <div
              key={i}
              style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                background: m.role === 'user' ? 'var(--primary)' : 'var(--paper)',
                color: m.role === 'user' ? '#fff' : 'var(--ink)',
                border: m.role === 'user' ? 'none' : '1px solid var(--line)',
                borderRadius: 14,
                padding: '8px 12px',
                maxWidth: '78%',
                fontSize: 14,
                lineHeight: 1.4,
                whiteSpace: 'pre-wrap',
              }}
            >
              {m.content ? (
                m.content
              ) : m.streaming ? (
                <span className="audiq-typing-dots" role="status" aria-label="Audiq sta scrivendo">
                  <span />
                  <span />
                  <span />
                </span>
              ) : (
                ''
              )}
            </div>
          ))}
          {sessionEnded && (
            <div
              className="mono"
              style={{
                alignSelf: 'center',
                color: 'var(--mute)',
                background: 'var(--soft)',
                padding: '6px 12px',
                borderRadius: 999,
                fontSize: 11,
              }}
            >
              Sessione terminata: {sessionEnded}
            </div>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendTurn();
          }}
          style={{
            padding: 12,
            borderTop: '1px solid var(--line)',
            background: 'var(--paper)',
            display: 'flex',
            gap: 8,
          }}
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={sessionEnded ? 'Sessione terminata' : 'Scrivi un messaggio…'}
            disabled={!sessionId || streaming || Boolean(sessionEnded)}
            className="flex-1 rounded-md border px-3 py-2 text-sm"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          />
          <button
            type="submit"
            disabled={!sessionId || streaming || !input.trim() || Boolean(sessionEnded)}
            className="rounded-md px-4 py-2 text-sm disabled:opacity-50"
            style={{ background: 'var(--primary)', color: '#fff' }}
          >
            {streaming ? '…' : 'Invia'}
          </button>
        </form>
      </div>
    </div>
  );
}
